import { LitElement, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-basic-card.inner.pcss?inline';
import { classMap } from 'lit/directives/class-map.js';
import '../se-link/se-link';
import '../se-heading/se-heading';
import { AllowedHeadingLevels } from '../se-heading/config';
import { SlotControllerLegacy } from '../../controllers/slot-controller-legacy';
import { whenParsed } from '../../utilities/when-parsed';

export const seBasicCardTypes = [
  'white',
  'green',
  'basic',
  'location',
] as const;
export type SeBasicCardType = typeof seBasicCardTypes[number];

/**
 * The Basic card component.
 * @element se-basic-card
 * @slot image - image slot
 */
@customElement('se-basic-card')
export class SeBasicCard extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });
  slots = new SlotControllerLegacy(this, true);

  /**
   *  Image card attributes
   */
  @property({ type: String })
  heading: string;
  @property({ type: String, attribute: 'heading-level' })
  headingLevel: AllowedHeadingLevels = 'h3';
  @property({ type: String, attribute: 'body-text' })
  bodyText: string;
  @property({ type: String })
  link: string;
  @property({ type: String, attribute: 'link-text' })
  linkText: string;
  @property({ type: String })
  type: SeBasicCardType = 'basic';
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'hover-animation',
  })
  hoverAnimation = false;

  @state() hasCardImage: boolean;

  async firstUpdated(): Promise<void> {
    // This component reads its own light-DOM children. In the critical
    // bundle it upgrades mid-parse, before those children exist, so wait
    // for them. See utilities/when-parsed.ts (SE-1004).
    await whenParsed();

    this.hasCardImage = this.slots.test('image');
  }

  hasHover(): TemplateResult {
    const classes = {
      'basic-card': true,
      [`basic-card--${this.type}`]: this.type,
      'basic-card--no-link': !this.link,
      'hover-img': true,
    };

    return html`<div
      class="${classMap(classes)}"
      @click="${this.clickHandler}"
      @mouseover="${this.hoverHandler}"
      @mouseleave="${this.hoverHandler}"
    >
      ${this.hasCardImage ? this.cardImage() : nothing} ${this.cardContent()}
    </div>`;
  }

  noHover(): TemplateResult {
    const classes = {
      'basic-card': true,
      [`basic-card--${this.type}`]: this.type,
      'basic-card--no-link': !this.link,
    };

    return html`<div class="${classMap(classes)}" @click="${this.clickHandler}">
      ${this.hasCardImage ? this.cardImage() : nothing} ${this.cardContent()}
    </div>`;
  }

  cardImage(): TemplateResult {
    return html`<div class="basic-card__image">
      <slot name="image"></slot>
    </div>`;
  }

  cardContent(): TemplateResult {
    return html`
      <div class="basic-card__content">
        ${this.heading
          ? html`<se-heading
              level="${this.headingLevel}"
              level-size="xl"
              level-style="normal"
              class="insight-card--title"
              >${this.heading}</se-heading
            >`
          : nothing}
        ${this.bodyText
          ? html`<p class="basic-card__body-text">
              ${unsafeHTML(this.bodyText)}
            </p>`
          : nothing}
        <slot></slot>
        ${this.linkText && this.link
          ? html`<se-link
              id="basic-card__link"
              class="basic-card__link"
              link-href="${this.link}"
              variant="${this.type !== 'green' ? 'cta' : 'cta-alt-2'}"
              >${this.linkText}</se-link
            >`
          : nothing}
      </div>
    `;
  }

  clickHandler() {
    if (this.link) {
      (
        this.shadowRoot
          ?.querySelector(`#basic-card__link`)
          ?.shadowRoot?.querySelector('a') as HTMLAnchorElement
      ).click();
    }
  }

  hoverHandler(event: MouseEvent) {
    const link = this.shadowRoot
      ?.querySelector(`#basic-card__link`)
      ?.shadowRoot?.querySelector('a');
    switch (event.type) {
      case 'mouseover':
        link?.classList.add('hover');
        break;
      case 'mouseleave':
        link?.classList.remove('hover');
        break;
    }
  }

  render(): TemplateResult {
    return html`${this.hoverAnimation ? this.hasHover() : this.noHover()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-basic-card': SeBasicCard;
  }
}
